import ContentElement from "./content-element.js";

interface CslContainerConfig {
    url: string;
    mode: number;
    delay: number;
    cache: number;
    cacheLifetime: number;
    cacheIdentifier: string;
    contextCssSelector: string;
    mediaMinWidth: number;
    mediaMaxWidth: number;
    placement: string;
    loader: boolean;
    lookingAhead: boolean;
}

const DEFAULT_ELEMENT_SELECTOR: string = '.th-fsc-csl';

const defaultOptions: CslContainerConfig = {
    url: '',
    mode: 0,
    delay: 0,
    cache: 0,
    cacheLifetime: 3600,
    cacheIdentifier: '',
    contextCssSelector: '',
    mediaMinWidth: 0,
    mediaMaxWidth: 0,
	placement: 'after',
	loader: true,
    lookingAhead: true
};


class CslContainer extends ContentElement {
    protected options: CslContainerConfig = defaultOptions;
    protected isLoaded: boolean = false;

    public override init() {
        this.options = this.assignConfig<CslContainerConfig>(defaultOptions, this.config);
    }

    public override process() {
        let loadContent: boolean = true;
        let windowWidth = window.screen.width;
        this.element.addEventListener('loadContent', () => {
            this.fetchContent();
        });
        if(this.options.mediaMinWidth > 0) {
            if(windowWidth < this.options.mediaMinWidth) {
                loadContent = false;
            }
        }
        if(this.options.mediaMaxWidth > 0) {
            if(windowWidth > this.options.mediaMaxWidth) {
                loadContent = false;
            }
        }

        if(!loadContent) {
            return;
        }

        let contextElement: HTMLElement|null = null;
        if(this.options.contextCssSelector) {
            let selector = this.options.contextCssSelector;
            let direction = 0;
            if(selector.substring(0, 1) === '^') {
                direction = 1;
                selector = selector.substring(1);
            } else if(selector.substring(0, 1) === '*') {
                direction = 2;
                selector = selector.substring(1);
            }

            contextElement = this.element.querySelector<HTMLElement>(selector);
            switch(direction) {
                case 1: // Query closest element
                    contextElement = this.element.closest<HTMLElement>(selector);
                    break;
                case 2: // Query document element
                    contextElement = document.querySelector<HTMLElement>(selector);
                    break;
            }
        }

        if(contextElement == null) {
            contextElement = this.element;
        }

        // Check Loading mode
        switch(this.options.mode) {
            case 0: // Load direct
                this.fetchContent();
            break;
            case 1: // Load if element is visible in viewport
                contextElement.setAttribute('data-loader', 'containerLoader');
                const intersectionObserver = new IntersectionObserver((entries) => {
                    if (entries[0].intersectionRatio <= 0) return;
                    this.fetchContent();
                });
                intersectionObserver.observe(contextElement);
            break;
            case 2: // Load if context element visible state changed
                let toObserve: HTMLElement | null = (contextElement != this.element) ? contextElement : null;
                if(toObserve == null) {
                    let parentElement: HTMLElement | null = this.element.parentElement;
                    while(parentElement != null) {
                        const parentElementRect = parentElement.getBoundingClientRect();
                        if(
                            ( parentElement.classList.contains('offcanvas') && parentElementRect.left < 0 )
                            || ( parentElement.style.display == 'none' )
                        ) {
                            toObserve = parentElement;
                            break;
                        }
                        parentElement = parentElement.parentElement;
                    }
                }
                if(toObserve != null) {
                    let toObserveLastelementRect = toObserve.getBoundingClientRect();
                    const observer = new MutationObserver((mutationList: MutationRecord[], observer: MutationObserver) => {
                        mutationList.forEach((mutation: MutationRecord) => {
                            const elementRect = toObserve.getBoundingClientRect();
                            if(mutation.type == 'attributes') {
                                if(
                                    toObserve.style.display != 'none'
                                    && (
                                        elementRect.left > -1
                                        || elementRect.left > toObserveLastelementRect.left
                                    )
                                ) {
                                    this.fetchContent();
                                }
                            }
                            toObserveLastelementRect = elementRect;
                        });
                    });
                    observer.observe(toObserve, {
                        attributes: true
                    });
                } else {
                    this.fetchContent();
                }
            break;
            case 3: // Load if a button has clicked
                const btnEl = (contextElement != this.element) ? contextElement : this.element.querySelector<HTMLElement>('.btn-load');
                if(btnEl) {
                    btnEl.addEventListener('click', () => {
                        this.fetchContent();
                    });
                }
            break;
        }
    }

    public loadContent() {
        this.fetchContent();
    }

    public get isContentLoaded() {
        return this.isLoaded;
    }

    private fetchContent() {
        if(!this.isLoaded && this.options.url != '') {
            let reloadContent = true;
            const ts: number = Math.round((new Date()).getTime() / 1000);
            let cacheStorage = (this.options.cache > 1) ? window.localStorage : window.sessionStorage;
            if(this.options.cache > 0 && cacheStorage) {
                const cacheDataJson: string|null = cacheStorage.getItem(this.options.cacheIdentifier);
                if(cacheDataJson) {
                    const cacheData = JSON.parse(cacheDataJson);
                    if(parseInt(cacheData?.lifetime) >= ts) {
                        reloadContent = false;
                        this.parseContent(cacheData.content);
                    } else {
                        cacheStorage.removeItem(this.options.cacheIdentifier);
                    }
                }
            }
            if(reloadContent) {
                let loaderEl: HTMLDivElement | null = null;
                if(this.options.loader) {
                    loaderEl = document.createElement('div');
                    loaderEl.setAttribute('class', 'd-flex justify-content-center');
                    loaderEl.innerHTML = '<div class="spinner-border text-th-gray" role="status"><span class="sr-only">Lade...</span></div>';
                }
                window.setTimeout(() => {
                    if(loaderEl) {
                        this.element.parentNode?.insertBefore(loaderEl, this.element);
                    }
                    fetch(this.options.url).then((response: Response) => {
                        if(!response.ok) {
                            throw Error(response.statusText);
                        }
                        return response.text();
                    }).then((content: string) => {
                        if(loaderEl) {
                            loaderEl.remove();
                        }
                        if(this.options.cache && cacheStorage) {
                            cacheStorage.setItem(this.options.cacheIdentifier, JSON.stringify({
                                content: content,
                                lifetime: ts + this.options.cacheLifetime
                            }));
                        }
                        this.parseContent(content);
                    }).catch((ex) => {
                        if(loaderEl) {
                            loaderEl.remove();
                        }
                        console.log(ex);
                        if(ex.status && ex.status > 0) {
                            window.ThiemeCore.errorHandler(
                                window.ThiemeCore.Lang.translate('common:error.request.'+ex.status+'.message', null, 'An error occurred while loading the content.'),
                                window.ThiemeCore.Lang.translate('common:error.request.'+ex.status+'.title', null, 'Error'),
                                'warning',
                                this.element
                            );
                        } else {
                            window.ThiemeCore.errorHandler(
                                window.ThiemeCore.Lang.translate('common:error.general.message', null, 'An error occurred while loading the content.'),
                                window.ThiemeCore.Lang.translate('common:error.general.title', null, 'Error'),
                                'warning',
                                this.element
                            );
                        }
                    });

                }, this.options.delay);
            }

            this.isLoaded = true;

            this.element.dispatchEvent(new Event('contentLoaded'));
        }
    }

    private parseContent(content: string) {
        const doc = (new DOMParser).parseFromString(content, 'text/html');

        const contentEl: HTMLElement|null = doc.querySelector<HTMLElement>('#th_csh_container_content');
        if(contentEl) {
            this.element.innerHTML = contentEl.innerHTML;

            const headEl: HTMLHeadElement = window.document.querySelector('head')!;
            const bodyEl: HTMLBodyElement = window.document.querySelector('body')!;
            doc.querySelectorAll<HTMLElement>('head *,body script').forEach((el: HTMLElement) => {
                if(el instanceof HTMLScriptElement) {
                    const newEl: HTMLScriptElement = document.createElement('script');
                    for(const idx in el.attributes) {
                        const attr = el.attributes[idx];
                        if(attr.value != undefined) {
                            newEl.setAttribute(attr.name, attr.value);
                        }
                    }
                    newEl.text = el.text;
                    if(el.closest('body')) {
                        bodyEl.appendChild(newEl);
                    } else {
                        headEl.appendChild(newEl);
                    }
                } else if(el instanceof HTMLStyleElement) {
                    headEl.appendChild(el);
                } else if(el instanceof HTMLLinkElement && el.getAttribute('rel') === 'stylesheet') {
                    headEl.appendChild(el);
                }
            });
            window.ThiemeMaintemplate.updateLayouts();
        }
    }
}

// Observe
CslContainer.initializeDom(DEFAULT_ELEMENT_SELECTOR, true, false, true);

export type { CslContainerConfig };
export default CslContainer;
